<template>
  <div class="w-full mt-4" :class="{ 'pointer-events-none': readOnly }">
    <Listbox
      v-if="activityFilter && !activityFilterSelected"
      v-model="selected_activity"
      :disabled="disabled"
      as="div"
      class="relative w-full">
      <div class="relative h-full">
        <ListboxButton
          class="relative w-full h-full py-1 pl-2 pr-10 overflow-hidden text-left bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
          <span v-if="!selected_activity" class="block py-1 truncate text-thin text-common-grey dark:text-dark-body-text">{{
            $t("Select Activity")
          }}</span>
          <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{ selected_activity }}</span>
          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </span>
        </ListboxButton>

        <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
          <ListboxOptions
            class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
            <ListboxOption
              v-for="activity in selected_activity_options"
              :key="activity"
              v-slot="{ active, selected }"
              as="template"
              :value="activity">
              <li
                :class="[
                  active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                  'cursor-default relative py-2 pl-5 pr-4 text-left has-tooltip'
                ]">
                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                  {{ activity }}
                </span>

                <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                  {{ activity }}
                </div>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>

    <div v-for="(clause_element, clause_index) in filter_restructured" :key="clause_index" class="w-full mt-1">
      <div class="relative w-full h-full pb-6 border border-dashed border-dark-brand-blue rounded-t-md bg-dark-brand-blue/10">
        <div v-if="clause_index" class="flex w-full absolute -top-[20px] z-10">
          <Switch
            :class="[
              clause_element.operator_with_prev_clause.operator.value == 'and'
                ? 'bg-[#9747FF]'
                : 'bg-[#FFBE15] dark:bg-dark-screen-background',
              'relative inline-flex flex-shrink-0 h-7 mx-auto w-[60px] border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
            ]"
            @update:modelValue="handleUpdateOperatorClause(clause_index)">
            <span
              aria-hidden="true"
              :class="[
                clause_element.operator_with_prev_clause.operator.value == 'and' && $store.state.portfolio.ui_options?.language == 'ar'
                  ? '-translate-x-8'
                  : clause_element.operator_with_prev_clause.operator.value == 'and'
                    ? 'translate-x-8'
                    : 'translate-x-0',
                'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
              ]"><span v-if="clause_element.operator_with_prev_clause.operator.value == 'and'" class="text-xs text-white -ml-14">{{
                 $t("AND")
               }}</span>
              <span v-else class="ml-8 text-xs text-white">{{ $t("OR") }}</span></span>
          </Switch>
        </div>
        <div class="relative flex items-center w-full h-10 m-2 space-x-2 text-sm">
          <div v-if="clause_element.metrics.length > 1">
            {{ $t("Match") }}
          </div>

          <div v-if="clause_element.metrics.length > 1" class="h-10">
            <ToggleFilterLogicClausesButton
              :current-clause-option="getClauseValue(clause_element.clause)"
              @update:currentClauseOption="($event) => selectClause($event, clause_index)" />
          </div>
          <div v-if="clause_element.metrics.length > 1">
            {{ $t("of the following criteria") }}
          </div>
          <button
            class="absolute font-normal btn right-4 cancel-btn border-dark-brand-blue"
            @click="addMetricToFilter($filter(all_metrics, { object_type: objectType || 'company' })[0], clause_index)">
            <Plus class="relative w-5 h-5 right-2 text-dark-brand-blue" />
            {{ $t("Add New criteria") }}
          </button>
        </div>

        <div class="mt-4">
          <div v-for="(metric_filter_element, metric_filter_index) in clause_element.metrics" :key="metric_filter_index">
            <div class="flex items-center w-full pl-2 pr-1 space-x-2">
              <div v-if="resultReferences?.length && !recordFilter && !activityFilter">
                <Listbox
                  v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].result_reference"
                  :disabled="disabled"
                  as="div"
                  class="relative flex-1">
                  <div class="relative h-full">
                    <ListboxButton
                      class="relative w-full h-full py-1 pl-3 pr-10 overflow-hidden text-center bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
                      <span
                        v-if="metric_filter_element.result_reference"
                        class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{ metric_filter_element.result_reference.name }}</span>
                      <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
                        $t("Result Reference")
                      }}</span>
                      <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>

                    <transition
                      leave-active-class="transition duration-100 ease-in"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0">
                      <ListboxOptions
                        class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
                        <ListboxOption v-if="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].result_reference">
                          <div class="flex justify-between">
                            <div
                              class="relative right-0 cursor-pointer text-common-grey top-2"
                              @click="
                                () => {
                                  selected_ref_name = null
                                  filter[findMetricIndexInFilter(clause_index, metric_filter_index)].result_reference = null
                                }
                              ">
                              {{ $t("Clear") }}
                            </div>
                          </div>
                        </ListboxOption>

                        <ListboxOption
                          v-for="option in resultReferences"
                          :key="option.name"
                          v-slot="{ active, selected }"
                          as="template"
                          :value="option"
                          @click="
                            () => {
                              selected_ref_name = option
                            }
                          ">
                          <li
                            :class="[
                              active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                              'cursor-default relative py-2 pl-5 pr-4 text-center'
                            ]">
                            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                              {{ option?.name }}
                            </span>

                            <span
                              v-if="selected"
                              :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                              <Check class="w-5 h-5" aria-hidden="true" />
                            </span>
                            <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                              {{ option?.label }}
                            </div>
                          </li>
                        </ListboxOption>
                      </ListboxOptions>
                    </transition>
                  </div>
                </Listbox>
              </div>

              <AutoCompleteInput
                v-if="$filter($store.state.metrics, { object_type: objectType || 'company' }).length && !selectedTrigger"
                class="w-[500px]"
                :value="metric_filter_element.name"
                :data="$filter(all_metrics, { object_type: objectType || 'company' })"
                :disabled="disabled"
                :placeholder="$t('Search for a Metric')"
                :is-box-background="isBoxBackground"
                @chosen="chooseMetric($event, clause_index, metric_filter_index)"
                @reset="resetMetric(clause_index, metric_filter_index)" />

              <Listbox
                v-if="other_metrics.find((metric) => metric_filter_element.reference_name?.includes(metric.reference_name))"
                v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].option"
                :class="{
                  'pointer-events-none opacity-30': !metric_filter_element.name
                }"
                :disabled="disabled"
                as="div"
                class="relative flex-1">
                <div class="relative h-full">
                  <ListboxButton
                    class="relative w-full h-full py-1 pl-3 pr-10 overflow-hidden text-center bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
                    <span
                      v-if="metric_filter_element.option"
                      class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{ metric_filter_element.option?.label }}</span>
                    <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
                      $t("Field")
                    }}</span>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </ListboxButton>

                  <transition
                    leave-active-class="transition duration-100 ease-in"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0">
                    <ListboxOptions
                      class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
                      <ListboxOption
                        v-for="option in metric_filter_element.options"
                        :key="option.value"
                        v-slot="{ active, selected }"
                        as="template"
                        :value="option"
                        @click="
                          metricSubNameChanged(
                            filter[findMetricIndexInFilter(clause_index, metric_filter_index)],
                            option,
                            clause_index,
                            metric_filter_index
                          )
                        ">
                        <li
                          :class="[
                            active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                            'cursor-default relative py-2 pl-5 pr-4 text-center'
                          ]">
                          <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                            {{ option?.label }}
                          </span>

                          <span
                            v-if="selected"
                            :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                            <Check class="w-5 h-5" aria-hidden="true" />
                          </span>
                          <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                            {{ option?.label }}
                          </div>
                        </li>
                      </ListboxOption>
                    </ListboxOptions>
                  </transition>
                </div>
              </Listbox>

              <div class="w-80">
                <Listbox
                  v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].operator"
                  :class="{
                    'pointer-events-none opacity-30': !metric_filter_element.name
                  }"
                  :disabled="disabled"
                  as="div"
                  class="relative w-full">
                  <div class="relative h-full">
                    <ListboxButton
                      class="relative w-full h-full py-1 pl-3 pr-10 overflow-hidden text-center bg-white border rounded-md cursor-default focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue dark:bg-dark-screen-background border-light-divider-border dark:border-dark-divider-border focus:outline-none">
                      <span
                        v-if="!metric_filter_element.operator"
                        class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{ $t("Condition") }}</span>
                      <span v-else class="block py-1 truncate text-thin text-light-body-text dark:text-dark-body-text">{{
                        metric_filter_element.operator?.label
                      }}</span>
                      <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>

                    <transition
                      leave-active-class="transition duration-100 ease-in"
                      leave-from-class="opacity-100"
                      leave-to-class="opacity-0">
                      <ListboxOptions
                        class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-max dark:bg-dark-screen-background max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none text-light-body-text dark:text-dark-body-text">
                        <ListboxOption
                          v-for="operator in metric_filter_element.operators"
                          :key="operator.value"
                          v-slot="{ active, selected }"
                          as="template"
                          :value="operator"
                          @click="metricOperatorChanged(filter[findMetricIndexInFilter(clause_index, metric_filter_index)])">
                          <li
                            :class="[
                              active ? 'text-white bg-blue' : 'text-light-body-text dark:text-dark-body-text',
                              'cursor-default relative py-2 pl-5 pr-4 text-center has-tooltip'
                            ]">
                            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                              {{ operator?.label }}
                            </span>

                            <span
                              v-if="selected"
                              :class="[active ? 'text-white' : 'text-blue', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                              <Check class="w-5 h-5" aria-hidden="true" />
                            </span>
                            <div class="flex-wrap p-2 text-white bg-gray-700 rounded tooltip whitespace-nowrap">
                              {{ operator?.label }}
                            </div>
                          </li>
                        </ListboxOption>
                      </ListboxOptions>
                    </transition>
                  </div>
                </Listbox>
              </div>

              <div
                v-if="
                  metric_filter_element?.operator?.value !== '__ISNULL__' && metric_filter_element?.operator?.value !== '__IS_NOT_NULL__'
                "
                class="w-full"
                :class="{
                  'pointer-events-none opacity-30': !metric_filter_element.operator
                }">
                <QuarterDatePicker
                  v-if="metric_filter_element?.operator?.value == '__DAYS_WITHIN_RANGE__'"
                  v-model:current-range="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  class="w-full mr-2 bg-white rounded-lg dark:bg-dark-box-background" />

                <input
                  v-else-if="
                    (metric_filter_element.data_type == 'Date' || metric_filter_element.data_type == 'DateTime') &&
                      metric_filter_element?.operator?.value.includes('__')
                  "
                  v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  :placeholder="$t('Number of days')"
                  min="1"
                  :disabled="disabled"
                  type="number"
                  class="flex-1 block w-full pl-5 my-2 overflow-hidden bg-white rounded-md border-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border"
                  @input="
                    (e) =>
                      $utils.debounce(() => {
                        e.target.value < 1
                          ? (filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value = '')
                          : filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value
                      }, 600)
                  ">

                <div
                  v-else-if="metric_filter_element.data_type == 'Date' || metric_filter_element.data_type == 'DateTime'"
                  class="z-20 w-full bg-white dark:bg-dark-screen-background">
                  <DatePicker
                    v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                    :is-dark="$store.state.ui.darkMode"
                    :model-config="{
                      type: 'string',
                      mask: 'YYYY-MM-DD'
                    }"
                    class="z-50 w-full px-1 pr-2 overflow-hidden bg-white dark:bg-dark-screen-background"
                    :popover="{ visibility: 'click' }">
                    <template #default="{ inputValue, inputEvents }">
                      <input
                        :disabled="disabled"
                        :value="inputValue"
                        class="w-full px-2 py-2 bg-white border rounded outline-none dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light"
                        v-on="inputEvents">
                    </template>
                  </DatePicker>
                </div>

                <input
                  v-else-if="metric_filter_element.data_type == 'Time'"
                  v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  type="time"
                  :disabled="disabled"
                  class="flex-grow w-full pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-dark-brand-blue dark:focus:ring-dark-brand-blue focus:border-dark-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                  :class="[
                    !isBoxBackground
                      ? 'bg-light-screen-background dark:bg-dark-screen-background'
                      : 'bg-light-box-background dark:bg-dark-box-background'
                  ]">

                <input
                  v-else-if="metric_filter_element.data_type == 'Number'"
                  v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  :placeholder="$t('Type a number')"
                  :disabled="disabled"
                  type="number"
                  class="flex-1 block w-full pl-5 my-2 overflow-hidden bg-white rounded-md border-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border">

                <input
                  v-else-if="metric_filter_element.data_type == 'Boolean'"
                  v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  :placeholder="$t('Type true or false')"
                  disabled
                  type="checkbox"
                  class="py-2 my-2 bg-white border rounded outline-none opacity-0 dark:bg-dark-screen-background text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:ring-blue-light focus:border-blue-light">

                <ComboBoxInput
                  v-else-if="metric_filter_element.reference_name == 'tags__name'"
                  v-model:model-value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  :name="`${findMetricIndexInFilter(clause_index, metric_filter_index)}-tags`"
                  :items="$store.state.data.tags"
                  label-attr="name"
                  value-attr="name"
                  :is-multiple="false"
                  :allow-null="true"
                  :return-value="true"
                  label=""
                  classes="w-full mr-2 relative dark:text-white"
                  input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
                  selection-text=""
                  :placeholder="$t('Assigned tags')"
                  :floating-label="false"
                  :show-label="false" />

                <ComboBoxInput
                  v-else-if="
                    metric_filter_element.reference_name == 'owner__email' ||
                      metric_filter_element.reference_name == 'co_owners__email' ||
                      (metric_filter_element.reference_name?.includes('_email') &&
                        other_metrics.find((metric) => metric_filter_element.reference_name?.includes(metric.reference_name)))
                  "
                  v-model:model-value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  :name="`owner-${findMetricIndexInFilter(clause_index, metric_filter_index)}`"
                  :is-multiple="false"
                  :disabled="disabled"
                  :items="$store.state.data.users"
                  classes="w-full mr-2 relative dark:text-white"
                  label-attr="full_name"
                  value-attr="email"
                  label=""
                  :placeholder="$t('Team members')"
                  :show-label="false"
                  :floating-label="false"
                  :allow-null="false">
                  <template #item="{ item, active }">
                    <li
                      :class="[
                        active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                        'cursor-default relative py-2 pl-4 pr-4 group/name'
                      ]">
                      <div class="flex items-center">
                        <img
                          v-if="item.avatar"
                          class="inline-block rounded-full h-9 w-9 object-cover"
                          :src="item.avatar"
                          onerror="this.onerror=null; this.src=window.default_avatar;"
                          alt="">
                        <Avatar
                          v-else
                          :hover-effect="true"
                          :first-name="item.first_name"
                          :last-name="item.last_name"
                          class="group-hover/name:bg-cyan-500" />
                        <div class="ml-3">
                          <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                            {{ Record.from(item).full_name }}
                          </p>
                          {{ item.email }}
                        </div>
                      </div>
                    </li>
                  </template>
                </ComboBoxInput>
                <div
                  v-else-if="
                    metric_filter_element?.option?.options &&
                      other_metrics.find((metric) => metric_filter_element.reference_name?.includes(metric.reference_name))
                  ">
                  <ComboBoxInput
                    v-model:model-value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                    :value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                    :disabled="disabled"
                    :is-multiple="false"
                    :name="`owner-${findMetricIndexInFilter(clause_index, metric_filter_index)}`"
                    :items="metric_filter_element.option.options"
                    classes="w-full mr-2 relative dark:text-white"
                    label-attr="label"
                    value-attr="value"
                    label=""
                    :placeholder="$t('Select item')"
                    :show-label="false"
                    :floating-label="false"
                    :allow-null="false">
                    <template #item="{ item, active }">
                      <li
                        :class="[
                          active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                          'cursor-default relative py-2 pl-4 pr-4 group/name'
                        ]">
                        <div class="flex items-center">
                          <div class="ml-3">
                            <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                              {{ item.label }}
                            </p>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ComboBoxInput>
                </div>

                <div v-else-if="metric_filter_element?.input_options?.choices">
                  <ComboBoxInput
                    v-model:model-value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                    :is-multiple="false"
                    :disabled="disabled"
                    :name="`option-${findMetricIndexInFilter(clause_index, metric_filter_index)}`"
                    :items="
                      metric_filter_element?.input_options?.choices.find((c) => c?.label)
                        ? $utils.clone(metric_filter_element?.input_options?.choices)
                        : $utils.clone(
                          metric_filter_element?.input_options?.choices.map((c, i) => {
                            return { value: c, label: c, id: i }
                          })
                        )
                    "
                    classes="w-full mr-2 relative dark:text-white"
                    label-attr="label"
                    value-attr="value"
                    label=""
                    :placeholder="$t('Select item')"
                    :show-label="false"
                    :floating-label="false"
                    :allow-null="false">
                    <template #item="{ item, active }">
                      <li
                        :class="[
                          active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                          'cursor-default relative py-2 pl-4 pr-4 group/name'
                        ]">
                        <div class="flex items-center">
                          <div class="ml-3">
                            <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                              {{ item.label }}
                            </p>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ComboBoxInput>
                </div>

                <div v-else-if="metric_filter_element.reference_name == 'type'">
                  <ComboBoxInput
                    v-model:model-value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                    :is-multiple="false"
                    :disabled="disabled"
                    :name="`option-${findMetricIndexInFilter(clause_index, metric_filter_index)}`"
                    :items="
                      $utils.clone(
                        $store.state.object_types.map((c, i) => {
                          return { value: `'${c.type}'`, label: c.type, id: i }
                        })
                      )
                    "
                    classes="w-full mr-2 relative dark:text-white"
                    label-attr="label"
                    value-attr="value"
                    label=""
                    :placeholder="$t('Select item')"
                    :show-label="false"
                    :floating-label="false"
                    :allow-null="false">
                    <template #item="{ item, active }">
                      <li
                        :class="[
                          active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                          'cursor-default relative py-2 pl-4 pr-4 group/name'
                        ]">
                        <div class="flex items-center">
                          <div class="ml-3">
                            <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                              {{ item.label }}
                            </p>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ComboBoxInput>
                </div>

                <div
                  v-else-if="
                    metric_filter_element.reference_name == 'parent' ||
                      metric_filter_element.reference_name == 'id' ||
                      metric_filter_element.reference_name == 'record'
                  ">
                  <ComboBoxInput
                    v-model:model-value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                    :is-multiple="false"
                    :disabled="disabled"
                    :name="`option-${findMetricIndexInFilter(clause_index, metric_filter_index)}`"
                    :items="
                      $utils.clone(
                        resultReferences
                          .filter((r) => (activityFilter ? r.type == selected_activity?.toLowerCase() : r.object_type))
                          .map((c, i) => {
                            return {
                              value: `'$\{${$utils.replaceLast(c.name.replaceAll(' ', '_').toLowerCase(), '_record', '__record')}.${'id'}}'`,
                              label: `${c.name}`,
                              id: i
                            }
                          })
                      )
                    "
                    classes="w-full mr-2 relative dark:text-white"
                    label-attr="label"
                    value-attr="value"
                    label=""
                    :placeholder="$t('Select item')"
                    :show-label="false"
                    :floating-label="false"
                    :allow-null="false">
                    <template #item="{ item, active }">
                      <li
                        :class="[
                          active ? 'text-white bg-blue' : 'text-gray-900 dark:text-white',
                          'cursor-default relative py-2 pl-4 pr-4 group/name'
                        ]">
                        <div class="flex items-center">
                          <div class="ml-3">
                            <p class="h-full text-sm font-normal text-gray-700 dark:text-white group-hover/name:text-white">
                              {{ item.label }}
                            </p>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ComboBoxInput>
                </div>

                <RecordAutoCompleteInput
                  v-else-if="metric_filter_element.reference_name == 'parent__id' || metric_filter_element.reference_name == 'parent__name'"
                  :filter-metric-ids="[$utils.getMetricByReferenceName($utils.getOptionMetricReference('company', 'name')).id]"
                  :is-async="true"
                  :placeholder="``"
                  :label="''"
                  :is-box-background="true"
                  :disabled="
                    $route.name == 'CompanyProfile' || ($route.name == 'ObjectProfile' && $route.params.object_type == 'opportunity')
                  "
                  name="parent__name"
                  type="text"
                  label-attr="name"
                  value-attr="id"
                  classes="w-full block border-none p-0"
                  :value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  @chosen="
                    ($event) => {
                      filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value = $event.id
                      filter[findMetricIndexInFilter(clause_index, metric_filter_index)].reference_name = 'parent__id'
                    }
                  "
                  @changed="
                    ($event) => {
                      if (!$event?.id) {
                        metric_filter_element.reference_name = 'parent__name'
                        filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value = $event
                        filter[findMetricIndexInFilter(clause_index, metric_filter_index)].reference_name = 'parent__name'
                      } else {
                        metric_filter_element.reference_name = 'parent__id'
                        filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value = $event.id
                        filter[findMetricIndexInFilter(clause_index, metric_filter_index)].reference_name = 'parent__id'
                      }
                    }
                  " />

                <AutoCompleteInput
                  v-else
                  v-model="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  class="w-full bg-transparent border-none"
                  :value="filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value"
                  :endpoint-async-url="getMetricSuggestionUrl(metric_filter_element)"
                  :is-box-background="isBoxBackground"
                  :is-async="
                    metric_filter_element.input_type == 'Select' && metric_filter_element.input_options?.choices.length > 0 ? false : true
                  "
                  :data="
                    metric_filter_element.input_type == 'Select' && metric_filter_element.input_options?.choices.length > 0
                      ? metric_filter_element.input_options?.choices
                      : metric_filter_element.suggestions
                  "
                  :disabled="filter[filter.length - 1]?.item_type == 'operator' || disabled"
                  :placeholder="
                    metric_filter_element?.operator?.value == 'in' || metric_filter_element?.operator?.value == '__EXCLUDES__'
                      ? $t('Type word/s separated by comma')
                      : $t('Type a word or a sentence')
                  "
                  type="text"
                  classes="relative text-light-body-text dark:text-dark-body-text bg-white dark:bg-dark-screen-background rounded-md"
                  input-classes="overflow-hidden  focus:outline-none focus:ring-blue-light focus:border-blue-light  w-full rounded-md border-light-divider-border dark:border-dark-divider-border"
                  @changed="
                    ($event) => {
                      filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value = $event
                    }
                  "
                  @chosen="
                    ($event) => {
                      filter[findMetricIndexInFilter(clause_index, metric_filter_index)].value = $event
                    }
                  " />
              </div>

              <!-- <button class="btn cancel-btn border-dark-brand-blue" @click="addMetricToFilter($filter(all_metrics, { object_type: objectType })[0], index)">+ Add</button> -->

              <Trash2
                :class="{
                  'w-6 h-6':
                    metric_filter_element?.operator?.value == '__ISNULL__' || metric_filter_element?.operator?.value == '__IS_NOT_NULL__'
                }"
                class="w-12 h-12 ml-2 cursor-pointer text-common-grey hover:text-red-500"
                aria-hidden="true"
                @click="removeMetricFromFilter(clause_index, metric_filter_index)" />
            </div>
          </div>
        </div>

        <div
          v-if="clause_index == filter_restructured.length - 1"
          class="w-full border-b border-dark-brand-blue h-1 absolute -bottom-[1px]" />
        <div v-if="clause_index == filter_restructured.length - 1" class="flex w-full absolute -bottom-[20px]">
          <button
            class="mx-auto font-normal btn cancel-btn border-dark-brand-blue"
            @click="addLogicOperator($store.state.data.filter_logic_operators[0])">
            <Plus class="relative w-5 h-5 right-2 text-dark-brand-blue" />
            {{ $t("Add new set of criteria") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RecordAutoCompleteInput from "@/components/shared/RecordAutoCompleteInput.vue"

import { DatePicker } from "v-calendar"
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue"

import AutoCompleteInput from "@/components/shared/AutoCompleteInput.vue"
import { Switch } from "@headlessui/vue"
import { ChevronDown, Check, Plus, Trash2 } from "lucide-vue-next"

import { Record } from "@/models/record"
import json_data from "@/components/shared/json-files/all-obj-fields.json"
import { transformServerFilterToMetricFilter, transformMetricFilterToServerFilter } from "@/services/data.transformers"

import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import Avatar from "@/components/shared/Avatar.vue"

import ToggleFilterLogicClausesButton from "@/components/shared/ToggleFilterLogicClausesButton.vue"
import QuarterDatePicker from "@/components/shared/QuarterDatePicker.vue"

export default {
  components: {
    RecordAutoCompleteInput,
    QuarterDatePicker,
    DatePicker,
    Check,
    Trash2,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    AutoCompleteInput,
    Switch,
    ChevronDown,
    Plus,
    ComboBoxInput,
    Avatar,
    ToggleFilterLogicClausesButton
  },
  props: {
    resultReferences: null,
    selectedTrigger: null,
    readOnly: null,
    currentFilter: null,
    recordFilter: null,
    activityFilter: null,
    activityFilterSelected: null,

    initialFilter: {
      type: String,
      default: ""
    },

    disabled: {
      type: Boolean,
      default: false
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    objectType: {
      type: String,
      required: false
    }
  },
  emits: ["update", "filter"],
  data() {
    return {
      selected_activity: null,
      selected_activity_options: ["Plan", "Goal", "Task", "Interaction"],
      Record,
      filter: [],
      other_metrics: [],
      forecasted_objects: [],
      selected_ref_name: null
    }
  },

  computed: {
    filter_with_uuids() {
      return this.filter.map((f) => {
        return { ...f, uuid: this.$uuid() }
      })
    },
    filter_restructured() {
      const filter_restructured_calc = []
      //initialize current filter inside clause
      let current_filter_inside_clause = {
        clause: null,
        metrics: [],
        operator_with_prev_clause: null
      }
      this.filter_with_uuids.forEach((filter_item, index) => {
        //inside a new clause
        if (filter_item.item_type == "clause") {
          //push previous filter clause if available and empty metrics for next filter
          if (current_filter_inside_clause.clause && current_filter_inside_clause.metrics.length) {
            filter_restructured_calc.push(this.$utils.clone(current_filter_inside_clause))
            current_filter_inside_clause.metrics = []
          }
          if (index && this.filter_with_uuids[index - 1].item_type == "operator") {
            current_filter_inside_clause.operator_with_prev_clause = this.$utils.clone(this.filter_with_uuids[index - 1])
          }
          current_filter_inside_clause.clause = this.$utils.clone(filter_item)
        } else if (filter_item.item_type == "metric" || filter_item.reference_name) {
          current_filter_inside_clause.metrics.push(this.$utils.clone(filter_item))
        }

        //If end of last clause
        if (index == this.filter_with_uuids.length - 1) {
          filter_restructured_calc.push(current_filter_inside_clause)
        }
      })

      return filter_restructured_calc
    },

    all_metrics() {
      let forecast_metrics = []
      if (this.forecasted_objects?.includes(this.objectType || "company")) {
        forecast_metrics = [
          {
            reference_name: `forecasts__forecast_status`,
            name: this.$t("Forecast Status"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["forecasted", "actual"] },
            object_type: this.objectType || "company"
          },
          {
            reference_name: `forecasts__forecast_type`,
            name: this.$t("Forecast Type"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["non-renewable", "renewable"] },
            object_type: this.objectType || "company"
          },
          {
            reference_name: `forecasts__forecast_value`,
            name: this.$t("Forecast Value"),
            data_type: "Number",
            input_type: "Number",
            object_type: this.objectType || "company"
          }
        ]
      }
      let parent_filters = [
        {
          type: "parent",
          name: this.$t("Direct Parent"),
          id: "_parent",
          reference_name: "parent__id",
          data_type: "String",
          object_type: this.objectType || "company"
        }
      ]
      let owner_co_owner_filer = [
        {
          reference_name: `owner__email`,
          name: this.$t("Owner"),
          data_type: "String",
          input_type: "Text",
          object_type: this.objectType || "company"
        },
        {
          reference_name: `co_owners__email`,
          name: this.$t("Co-Owner"),
          data_type: "String",
          input_type: "Text",
          object_type: this.objectType || "company"
        }
      ]
      let tags_filter_fields = [
        {
          reference_name: `tags__name`,
          name: this.$t("Assigned tags"),
          data_type: "String",
          input_type: "Text",
          object_type: this.objectType || "company"
        }
      ]

      let static_options_for_playbook = []

      if (this.recordFilter) {
        static_options_for_playbook.push({
          reference_name: `id`,
          name: this.$t("Record ID"),
          data_type: "String",
          input_type: "Select",
          input_options: { choices: this.resultReferences?.map((x) => x.id) || [] },
          object_type: this.objectType || "company"
        })

        static_options_for_playbook = [
          {
            reference_name: `type`,
            name: this.$t("Object Type"),
            data_type: "Playbook",
            input_type: "Text",
            object_type: this.objectType || "company"
          }
        ]

        if (this.resultReferences?.length) {
          static_options_for_playbook.push({
            reference_name: `parent`,
            name: this.$t("Parent"),
            data_type: "Playbook",
            input_type: "Text",
            object_type: this.objectType || "company"
          })
          static_options_for_playbook.push({
            reference_name: `id`,
            name: this.$t("Result Reference"),
            data_type: "Playbook",
            input_type: "Text",
            object_type: this.objectType || "company"
          })
        }
      }

      if (this.activityFilter) {
        static_options_for_playbook.push({
          reference_name: `id`,
          name: this.$t("Result Reference"),
          data_type: "Playbook",
          input_type: "Text",
          object_type: this.objectType || "company"
        })

        return this.other_metrics.filter((m) => m.data_type == this.selected_activity).concat(static_options_for_playbook)
      }

      return this.$store.state.metrics
        .concat(forecast_metrics)
        .concat(owner_co_owner_filer)
        .concat(parent_filters)
        .concat(this.other_metrics)
        .concat(tags_filter_fields)
        .concat(static_options_for_playbook)
    }
  },
  watch: {
    activityFilterSelected: {
      deep: true,
      handler() {
        this.selected_activity = this.activityFilterSelected
      }
    },
    currentFilter: {
      deep: true,
      handler() {
        this.initFilter()
      }
    },

    filter: {
      deep: true,
      handler() {
        if (this.filter.length <= 1) {
          if (this.objectType) {
            this.addMetricToFilter(
              this.$filter(this.all_metrics, {
                object_type: this.objectType || "company"
              })[0]
            )
          } else {
            this.addMetricToFilter(this.all_metrics[0])
          }
        }
        let filter = this.filter.map((item) => {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (item.operator?.value == "__DAYS_WITHIN_RANGE__") {
            item["modified_value"] = `('${this.$moment(item.value.start).format("YYYY-MM-DD")}','${this.$moment(item.value.end).format(
              "YYYY-MM-DD"
            )}')`
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator?.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator?.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator?.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator?.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator?.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator?.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          return item
        })
        // this.$emit("change", { filter: parsed_filter.filter, valid: parsed_filter.valid })
        this.$emit("update", filter)
        let parsed_filter = transformMetricFilterToServerFilter(filter)
        this.$emit("filter", parsed_filter.filter)
      }
    }
  },

  created() {
    this.init()
    if (this.activityFilterSelected) {
      this.selected_activity = this.activityFilterSelected
    }
  },

  methods: {
    getMetricSuggestionUrl(metric) {
      return `/api/organizations/${this.$store.state.organization.id}/metrics/${metric.id}/suggestions/`
    },

    init() {
      this.forecasted_objects = this.$utils
        .find(this.$store.state.settings.modules_settings, {
          reference_code: "renewal_base_metric"
        })
        ?.mapped_objects_metrics?.map((type_map) => {
          return this.$store.state.object_types.find((x) => x.id == type_map.object_type)?.type
        })
      Object.entries(json_data).map((item) => {
        this.other_metrics.push({
          ...item[1],
          object_type: this.objectType || "company"
        })
      })
      this.initFilter()
      if (!this.initialFilter && !this.currentFilter) {
        {
          this.addMetricToFilter(
            this.$filter(this.all_metrics, {
              object_type: this.objectType || "company"
            })[0]
          )
        }
      }

      // console.log( this.all_metrics.find(m=>m.name.toLowerCase()== this.selectedTrigger.replaceAll("_"," ")), "FOUNDD")
      if (this.selectedTrigger && !this.initialFilter) {
        const metric_calc = this.all_metrics.find((m) => m.name.toLowerCase() == this.selectedTrigger.replaceAll("_", " "))
        this.chooseMetric(metric_calc, 0, 0)
      }

      if (this.activityFilter && this.initialFilter) {
        if (this.initialFilter.includes("plan")) {
          this.selected_activity = "Plan"
        }
        if (this.initialFilter.includes("goal")) {
          this.selected_activity = "Goal"
        }
        if (this.initialFilter.includes("task")) {
          this.selected_activity = "Task"
        }
        if (this.initialFilter.includes("interaction")) {
          this.selected_activity = "Interaction"
        }
      }
    },

    handleUpdateOperatorClause(clause_index) {
      const operator_obj = this.filter_restructured[clause_index].operator_with_prev_clause
      const index = this.filter_with_uuids.findIndex((f) => f?.uuid == operator_obj?.uuid)
      this.filter.splice(index, 1, {
        ...this.filter[index],
        operator:
          this.filter[index].operator.value == "and"
            ? this.$store.state.data.filter_logic_operators[1]
            : this.$store.state.data.filter_logic_operators[0]
      })
    },

    getClauseValue(clause_obj) {
      if (this.$store.state.data.filter_logic_clauses.find((l) => l.value == clause_obj?.clause?.value)) {
        return this.$store.state.data.filter_logic_clauses.find((l) => l.value == clause_obj?.clause.value).label
      }
      return this.$store.state.data.filter_logic_clauses[0].label
    },

    selectClause(clause, clause_index) {
      const clause_modified = clause == "All" ? clause : `${clause} of`
      const clause_filter_obj = this.filter_restructured[clause_index].clause
      const index = this.filter_with_uuids.findIndex((f) => f?.uuid == clause_filter_obj?.uuid)
      const clause_obj = this.$store.state.data.filter_logic_clauses.find((c) => c.label == clause_modified)

      this.filter.splice(index, 1, {
        ...this.filter[index],
        clause: clause_obj
      })
    },

    metricOperatorChanged(metric) {
      if (metric.operator.value == "__ISNULL__") {
        metric.value = "True"
      }
      if (metric.operator.value == "__IS_NOT_NULL__") {
        metric.value = "False"
      }
    },
    metricSubNameChanged(metric, option, clause_index, metric_filter_index) {
      setTimeout(() => {
        let arr = metric.reference_name.split("__")
        metric.reference_name = `${arr[0] + "__"}`.concat(option.value)
        metric.data_type = option.data_type
        metric.input_type = option.input_type
        this.chooseMetric(metric, clause_index, metric_filter_index)
      }, 600)
    },

    addMetricToFilter(in_metric, clause_index) {
      let metric = { ...in_metric }
      metric = this.injectOperators(metric)

      if (this.selectedTrigger) {
        metric = this.all_metrics.find((m) => m.name.toLowerCase() == this.selectedTrigger.replaceAll("_", " "))
      }

      if (clause_index !== undefined) {
        const last_metric_in_clause =
          this.filter_restructured[clause_index].metrics[this.filter_restructured[clause_index].metrics.length - 1]
        const index = this.filter_with_uuids.findIndex((f) => f?.uuid == last_metric_in_clause?.uuid)
        if (this.selectedTrigger) {
          this.filter.splice(index + 1, 0, { ...metric })
        } else {
          this.filter.splice(index + 1, 0, { ...metric, name: "" })
        }
      } else {
        if (this.selectedTrigger) {
          this.filter.push({ ...metric })
        } else {
          this.filter.push({ ...metric, name: "" })
        }
      }
    },

    chooseMetric(e, clause_index, metric_filter_index) {
      let metric = { ...e }
      metric = this.injectOperators(metric)
      const index = this.findMetricIndexInFilter(clause_index, metric_filter_index)
      this.filter.splice(index, 1, metric)
    },
    findMetricIndexInFilter(clause_index, metric_filter_index) {
      const element = this.filter_restructured[clause_index].metrics[metric_filter_index]
      const index = this.filter_with_uuids.findIndex((item) => item?.uuid == element?.uuid)
      return index
    },

    resetMetric(clause_index, metric_filter_index) {
      const index = this.findMetricIndexInFilter(clause_index, metric_filter_index)
      this.filter.splice(index, 1, { ...this.filter[index], name: "" })
    },
    removeMetricFromFilter(clause_index, metric_filter_index) {
      const index = this.findMetricIndexInFilter(clause_index, metric_filter_index)

      if (index == 1 && this.filter[index + 1]?.item_type == "operator") {
        if (!this.filter[index + 2]?.reference_name) {
          this.filter.splice(index, 3)
        } else {
          this.filter.splice(index, 2)
        }
      } else if (this.filter[index - 1]?.item_type == "operator") {
        this.filter.splice(index - 1, 2)
      } else if (this.filter[index - 1]?.item_type == "clause" && this.filter[index - 2]?.item_type == "operator") {
        this.filter.splice(index - 2, 3)
        if (this.filter.length <= 1) {
          {
            this.addMetricToFilter(
              this.$filter(this.all_metrics, {
                object_type: this.objectType || "company"
              })[0]
            )
          }
        } else {
          this.filter.splice(index, 1)
        }

        if (this.filter.length <= 1) {
          this.addMetricToFilter(
            this.$filter(this.all_metrics, {
              object_type: this.objectType || "company"
            })[0]
          )
        }
      } else {
        this.filter.splice(index, 1)
      }
    },

    injectOperators(metric) {
      let type = metric.data_type?.toLowerCase()
      if (type == "datetime" || type == "date") {
        type = "date"
      }
      let value = ""
      // For boolean the value is allways true, but the operator
      // can be == or != ie: == true or != true which is false
      if (type == "boolean") {
        value = "true"
      }
      metric["operators"] = this.$store.state.data.filter_operators.filter((o) => o.for.split("_").findIndex((t) => t == type) != -1)
      metric["operator"] = null
      metric["value"] = value
      metric["item_type"] = "metric"
      if (this.selected_ref_name) {
        metric["result_reference"] = this.selected_ref_name
      }
      return metric
    },

    addLogicOperator(operator) {
      this.filter.push({ item_type: "operator", operator })
      this.filter.push({
        item_type: "clause",
        clause: this.$store.state.data.filter_logic_clauses[0]
      })
      this.addMetricToFilter(this.$filter(this.all_metrics, { object_type: this.objectType || "company" })[0])
    },

    initFilter() {
      if (this.initialFilter) {
        let filter = transformServerFilterToMetricFilter(this.$utils.clone(this.initialFilter), null, null, this.resultReferences)
        //change logic to know if its simple or not
        this.filter = filter.filter
      } else if (this.currentFilter) {
        let filter = transformServerFilterToMetricFilter(this.$utils.clone(this.currentFilter), null, null, this.resultReferences)
        //change logic to know if its simple or not
        this.filter = filter.filter
      } else {
        this.filter = [
          {
            item_type: "clause",
            clause: { label: this.$t("All"), value: "(and", inner_operator: "and" }
          }
        ]
      }
    }
  }
}
</script>

<style scoped lang="postcss">
.ghost {
  @apply bg-gray-300 border-0 w-full;
}
.chosen {
  @apply bg-transparent border-0;
}
.chosen .line {
  opacity: 0;
}
</style>
